/**
 * English strings — the canonical catalog. Every other locale must
 * match this shape; missing keys fall back to English at runtime.
 *
 * Keys are stable. Don't rename them. When a string changes, keep the
 * key, update the value; translators will re-translate next pass.
 */

import type { LocaleCode } from '../locales'

/**
 * Explicit type so non-English catalogs aren't constrained to the
 * English literal values. English is the reference implementation —
 * translations must match the shape, not the text.
 */
export interface StringCatalog {
  paywall: {
    cta: {
      startTrial:      string
      startProPlus:  string
      startProYearly:  string
      startPro:        string
      continueEmail:   string
      loading:         string
      sending:         string
      checkInbox:      string
      trialHint:       string
      noTrialHint:   string
      magicLinkHint:   string
      promoPrompt:     string
      promoPlaceholder:string
      emailPlaceholder:string
    }
    billing: {
      monthly:        string
      yearly:         string
      saveBadgeShort: string
      perMonth:       string
      pricedMonthly:  (p: string) => string
      saveLine:       string
      billedYearly:   (yearly: string) => string
      billedMonthly:  string
    }
    footer: {
      stripeSecured:   string
      cancelAnytime:   string
      noHiddenFees:    string
      oneSubscription: string
    }
    /** "Works everywhere" note shown under the plan table. */
    crossPlatform: {
      note: string
    }
    features: {
      chars:         string
      slang:         string
      media:         string
      voice:         string
      crossPlatform: string
    }
    reasons: Record<
      | 'upgrade' | 'monthly-chars-reached' | 'text-too-long' | 'slang-mode'
      | 'summary-mode' | 'pdf' | 'image' | 'document'
      | 'voice' | 'word-alternatives' | 'favorites' | 'history'
      | 'text-tutor-pro-limit' | 'conversation' | 'situations' | 'voice-tutor' | 'text-tutor-limit' | 'reply-drafts',
      { badge: string; title: string; subtitle: string }
    >
    /** Per-reason bullet list shown in place of the fixed feature icons — mirrors mobile's FEATURE_LOCK_CONFIGS.features. */
    reasonFeatures: Record<
      | 'upgrade' | 'monthly-chars-reached' | 'text-too-long' | 'slang-mode'
      | 'summary-mode' | 'pdf' | 'image' | 'document'
      | 'voice' | 'word-alternatives' | 'favorites' | 'history'
      | 'text-tutor-pro-limit' | 'conversation' | 'situations' | 'voice-tutor' | 'text-tutor-limit' | 'reply-drafts',
      string[]
    >
    picker: {
      label:       string
      change:      string
      currentlyEn: string
    }
    /** Pro / Pro+ segmented control shown above the plan table — mirrors mobile's PaywallTierToggle. Only rendered when pricing.proPlus exists. */
    tierToggle: {
      pro:     string
      proPlus: string
      badge:   string
    }
    /** Additive Pro+ benefits block (Pro baseline + Plus differentiator) — mirrors mobile's PaywallProPlusBenefits. Shown when the Pro+ tier is selected. */
    proPlusBenefits: {
      label:      string
      proBadge:   string
      plusBadge:  string
      proHeader:  string
      plusHeader: string
      proItems:   string[]
      plusItems:  string[]
    }
    /** Compact Pro vs Pro+ comparison shown below the plan table when pricing.proPlus exists. */
    proPlusComparison: {
      label:      string
      proHeader:  string
      plusHeader: string
      rows: { label: string; pro: string; proPlus: string }[]
    }
  }
  limits: {
    monthlyReached:    string
    monthlyReachedPro: string
    tooLong:           string
    voiceMonthly:      string
  }
  magicLink: {
    sending:     string
    sent:        string
    sentDesc:    string
    invalid:     string
    failed:      string
    placeholder: string
  }
  common: {
    close:  string
    cancel: string
    back:   string
    retry:  string
  }
}

export const strings: StringCatalog = {
  /** Paywall modal — applies to all reasons. */
  paywall: {
    cta: {
      startTrial:      'Start 30-Day Free Trial',
      startProPlus:    'Upgrade to Pro+',
      startProYearly:  'Start Pro and Save $96 a Year',
      startPro:        'Start Pro',
      continueEmail:   'Continue with email →',
      loading:         'Loading…',
      sending:         'Sending…',
      checkInbox:      '✓ Check your inbox',
      trialHint:       'First 30 days free. Cancel anytime.',
      noTrialHint:     'Pro+ has no free trial: it is billed today. Cancel anytime.',
      magicLinkHint:   "We'll email you a sign-in link. No password needed.",
      promoPrompt:     'Have a promo code?',
      promoPlaceholder:'PROMO CODE',
      emailPlaceholder:'your@email.com',
    },
    billing: {
      monthly:        'Monthly',
      yearly:         'Yearly',
      saveBadgeShort: '7 Months Free',
      perMonth:       '/month',
      pricedMonthly:  (p: string) => `${p}/mo`,
      saveLine:       'Save $96/year',
      billedYearly:   (yearly: string) => `Billed as ${yearly}/year · 3M chars/year`,
      billedMonthly:  'Billed monthly · cancel anytime · 250K chars/mo',
    },
    footer: {
      stripeSecured:  'Secured by Stripe',
      cancelAnytime:  'Cancel anytime',
      noHiddenFees:   'No hidden fees',
      oneSubscription:'One subscription covers the web app, the Chrome extension, iPhone, and Android.',
    },
    crossPlatform: {
      note: 'Pro works on iPhone and Android too. Sign in with the same email.',
    },
    features: {
      chars:          '250,000 characters a month (3M a year on annual plans)',
      slang:          'Israeli slang and summary modes',
      media:          '40 images and 10 PDFs a month',
      voice:          'Listen to any translation with premium voices',
      crossPlatform:  'One subscription for the web app, Chrome extension, iPhone, and Android',
    },
    /** Per-reason copy. Keep keys aligned with PaywallReason union. */
    reasons: {
      upgrade: {
        badge:    'baba Pro',
        title:    'Upgrade to baba Pro',
        subtitle: 'Unlock higher limits, slang, and voice.',
      },
      'monthly-chars-reached': {
        badge:    "You're Out of Characters",
        title:    'Get Higher Limits',
        subtitle: 'Translate more text, images, PDFs, and voice.',
      },
      'text-too-long': {
        badge:    'This Text Is Too Long',
        title:    'Translate Longer Texts',
        subtitle: 'Go from 500 to 20,000 characters per translation.',
      },
      'slang-mode': {
        badge:    'Pro Feature: Slang Mode',
        title:    'Unlock Every Slang Entry',
        subtitle: 'Go beyond the list. See full definitions, examples, and context.',
      },
      'summary-mode': {
        badge:    'Pro Feature: Summaries',
        title:    'Summarize Long Texts',
        subtitle: 'Get the key points without reading everything.',
      },
      pdf: {
        badge:    'Pro Feature: PDF Translation',
        title:    'Translate PDF Documents',
        subtitle: 'Upload a PDF (up to 20 pages, 10 MB) and get a clean translation. Up to 10 PDFs a month on Pro.',
      },
      image: {
        badge:    'Pro Feature: Image Translation',
        title:    'Translate Any Image',
        subtitle: 'Point your camera at a sign, menu, or screenshot. Up to 40 images a month on Pro.',
      },
      document: {
        badge:    'Pro Feature: Document Translation',
        title:    'Translate PDFs & Documents',
        subtitle: 'Upload a PDF, Word, or PowerPoint file (up to 10 MB) and get a clean translated version back, formatting kept where possible. Up to 15 documents a month on Pro.',
      },
      voice: {
        badge:    'Pro Feature: Voice Playback',
        title:    'Hear Hebrew Out Loud',
        subtitle: 'Listen to natural pronunciation for any translation. Double-click any word to hear just that word.',
      },
      'word-alternatives': {
        badge:    'Pro Feature: Alternatives',
        title:    'See Other Ways to Say It',
        subtitle: 'Get alternative phrasings for any translation, in context. Great for learning nuance.',
      },
      favorites: {
        badge:    'Pro Feature: Saved Translations',
        title:    'Save Every Phrase Worth Keeping',
        subtitle: 'Free caps saved phrases at 50. Pro keeps them all, so you can build your own phrasebook.',
      },
      history: {
        badge:    'Pro Feature: Translation History',
        title:    'Find Every Translation You Ever Made',
        subtitle: 'Free keeps your last 200 translations. Pro keeps every one, searchable on all your devices.',
      },
      'text-tutor-limit': {
        badge:    'Pro Feature: Avi Tutor',
        title:    'Keep Learning With Avi',
        subtitle: 'You used your 15 free Avi replies. Go Pro to keep the conversation going.',
      },
      'reply-drafts': {
        badge:    'Pro Feature: Reply Drafts',
        title:    'Reply in Hebrew Without Guessing',
        subtitle: 'You used your three free reply drafts this month.',
      },
      'text-tutor-pro-limit': {
        badge:    'Pro+ Feature: Avi Tutor',
        title:    'Get More Time With Avi',
        subtitle: 'You used your 300 Pro replies. Pro+ gives you 1,000 completed Avi replies every 30 days.',
      },
      conversation: {
        badge:    'Pro Feature: Conversation Mode',
        title:    "Talk, Don't Type",
        subtitle: 'Real-time voice conversations, two-way or one-way. Pro gets 15 minutes every 30 days, Pro+ gets 60.',
      },
      situations: {
        badge:    'Pro+ Feature: Daily Situations',
        title:    'Keep Your Daily Situations',
        subtitle: 'You used your three free Situations. Pro+ keeps one arriving every day.',
      },
      'voice-tutor': {
        badge:    'Pro+ Feature: Voice Tutor',
        title:    'Meet Avi',
        subtitle: 'Your live AI Hebrew tutor. Upgrade to Pro+ to start talking.',
      },
    },
    /** Per-reason feature bullets — mirrors mobile FEATURE_LOCK_CONFIGS.features, numbers adjusted to the web/server limits. */
    reasonFeatures: {
      upgrade: [
        'Translate up to 20,000 characters at once',
        'Slang, summary, and detailed modes unlocked',
        'Higher limits for text, images, PDFs, and voice',
      ],
      'monthly-chars-reached': [
        '250,000 characters a month (3M a year on annual plans)',
        '40 image translations a month',
        '10 PDF translations a month',
        '5,000 voice characters a month',
      ],
      'text-too-long': [
        'Translate up to 20,000 characters in one go',
        'Send full conversations and documents without splitting',
        'Avoid truncation and missing parts of your text',
      ],
      'slang-mode': [
        'Full definitions for every Hebrew slang term',
        'Real-world example sentences so you see it in context',
        "Save your favorites and request terms you've heard",
      ],
      'summary-mode': [
        'Translate and summarize in a single tap',
        'See key points instead of walls of text',
        'Perfect for articles, chats, and documents',
      ],
      pdf: [
        '10 PDF translations a month on Pro',
        'Full-document Hebrew to English translation',
        'Keep layout and structure as readable as possible',
      ],
      image: [
        '40 image translations a month on Pro',
        'Translate screenshots, menus, and signs',
        'Keep layout and context intact in results',
      ],
      document: [
        '15 document translations a month on Pro',
        'PDF, Word, and PowerPoint files up to 10 MB',
        'Formatting kept where possible',
      ],
      voice: [
        '5,000 voice characters a month on Pro',
        'Natural AI Hebrew voices',
        'Double-click any word to hear just that word',
      ],
      'word-alternatives': [
        'Alternative phrasings for any translation, in context',
        'See nuance you would miss with a single translation',
        'Great for sounding natural, not just correct',
      ],
      favorites: [
        'Unlimited saved phrases, Free caps at 50',
        'Build your own Hebrew phrasebook',
        'Synced across the web app, extension, and mobile',
      ],
      history: [
        'Every translation kept, Free caps at 200',
        'Search your full translation history',
        'Synced across the web app, extension, and mobile',
      ],
      'text-tutor-limit': [
        '300 completed Avi replies every 30 days',
        'Avi remembers the conversation and your learning context',
        'Add images, PDFs, and documents to your questions',
        'Everything in baba Pro is included',
      ],
      'reply-drafts': [
        'Two ready-to-send Hebrew replies for any message you receive',
        'Each one shown in your language too, so you know what you are sending',
        'Say what you mean and get it written properly in Hebrew',
        'Copy or send straight from the result screen',
      ],
      'text-tutor-pro-limit': [
        '1,000 completed Avi replies every 30 days',
        '60 minutes of live Voice Tutor every month',
        '60 minutes of live Conversation every month',
        'Everything in baba Pro is included',
      ],
      conversation: [
        'Two-way: two people talk and baba interprets both sides in real time',
        'One-way: you speak and baba says the translation out loud',
        'Perfect for travel, meeting locals, or closing a deal abroad',
      ],
      situations: [
        'One real Hebrew situation every day on WhatsApp',
        'baba plays the other person and corrects as you go',
        'Pronunciation with every correction',
        'Ninety situations, from ordering falafel to disputing a bill',
      ],
      'voice-tutor': [
        'Pro+ includes 60 tutor and 60 conversation minutes every month',
        'Real voice conversations with a native-feeling Israeli teacher',
        'Speaks the right form for you, masculine or feminine, set once and forget',
        'Practice ordering, asking directions, grammar, and slang, whatever you need',
        'Everything in baba Pro is included',
      ],
    },
    /** Pro / Pro+ segmented control shown above the plan table. */
    tierToggle: {
      pro:     'Pro',
      proPlus: 'Pro+',
      badge:   'AI',
    },
    /** Additive Pro+ benefits block, shown below the plan table when Pro+ is selected. */
    proPlusBenefits: {
      label:      'What You Get',
      proBadge:   'PRO',
      plusBadge:  'PRO+',
      proHeader:  'Everything in baba Pro',
      plusHeader: 'Plus: Avi, Your Tutor, and Daily Situations',
      proItems: [
        '20,000 characters per translation',
        'Slang, summary, and detailed modes',
        'Voice input plus 15 min conversation a month',
        'Image and PDF translation',
      ],
      plusItems: [
        '60 minutes of voice tutoring a month',
        '60 minutes of conversation mode a month',
        'Native-feeling Israeli teacher',
        'Practice slang, grammar, and directions, anything you need',
        'Set masculine or feminine voice once, forget it',
        'Daily Situations on WhatsApp',
      ],
    },
    /** Compact Pro vs Pro+ comparison, shown below the plan table when Pro+ is configured. */
    proPlusComparison: {
      label:      'Pro vs Pro+',
      proHeader:  'Pro',
      plusHeader: 'Pro+',
      rows: [
        { label: 'Hebrew Voice Tutor',   pro: 'Not Included',  proPlus: '60 min / month' },
        { label: 'Conversation Mode',    pro: '15 min / month', proPlus: '60 min / month' },
        { label: 'Avi Tutor Replies',    pro: '300 / month',   proPlus: '1,000 / month' },
        { label: 'Daily Situations',     pro: 'Not Included',  proPlus: 'Included' },
      ],
    },
    /** Picker UI */
    picker: {
      label:        'Language',
      change:       'Change language',
      currentlyEn:  'English',
    },
  },

  /** Limit-reached inline messages shown OUTSIDE the paywall modal. */
  limits: {
    monthlyReached:       "You've used up this month's character allowance. Upgrade to Pro for 100× more.",
    monthlyReachedPro:    "You've used up this period's character allowance.",
    tooLong:              'This passage is too long for the Free plan.',
    voiceMonthly:         'Monthly voice limit reached',
  },

  /** Magic-link auth prompts. */
  magicLink: {
    sending:   'Sending…',
    sent:      'Check your inbox',
    sentDesc:  'Click the link in the email to sign in. The link expires in 10 minutes.',
    invalid:   'Please enter a valid email',
    failed:    'Failed to send magic link',
    placeholder: 'you@example.com',
  },

  common: {
    close:  'Close',
    cancel: 'Cancel',
    back:   'Back',
    retry:  'Try again',
  },
}

/** Runtime fallback registry — maps locale code → catalog. */
export const catalogs: Partial<Record<LocaleCode, StringCatalog>> = {
  en: strings,
}

export default strings
